/**
 * Mordeczka.eu Admin - Series Form Page
 */

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Spin,
  message,
  Divider,
  Modal,
  Upload,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  worksApi,
  languagesApi,
  genresApi,
  streamingPlatformsApi,
  imagesApi,
  nationalitiesApi,
  directorsApi,
  type SeriesInfoResponse,
} from "../services/api";
import type { WorkCreate, WorkUpdate } from "../types";
import { AISeriesHelper } from "../components/AISeriesHelper";
import { SeasonsManager } from "../components/SeasonsManager";
import { WorkImagesManager } from "../components/WorkImagesManager";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;
const { TextArea } = Input;

export function SeriesFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [directorForm] = Form.useForm();
  const isEditing = Boolean(id);
  const { canCreate, canEdit } = usePermissions();

  const isReadOnly = isEditing ? !canEdit : !canCreate;

  // Modal state
  const [isDirectorModalOpen, setIsDirectorModalOpen] = useState(false);

  // Cover image state
  const [coverImageId, setCoverImageId] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    null,
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Search states for remote fetching
  const [directorSearch, setDirectorSearch] = useState("");
  const [debouncedDirectorSearch, setDebouncedDirectorSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedDirectorSearch(directorSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [directorSearch]);

  // Fetch work for editing
  const { data: work, isLoading: workLoading } = useQuery({
    queryKey: ["work", id],
    queryFn: () => worksApi.get(id!),
    enabled: isEditing,
  });

  // Fetch languages
  const { data: languages } = useQuery({
    queryKey: ["languages-all"],
    queryFn: () => languagesApi.list({ page_size: 1000 }),
  });

  // Fetch genres
  const { data: genres } = useQuery({
    queryKey: ["genres-all"],
    queryFn: () => genresApi.list({ page_size: 1000 }),
  });

  // Fetch streaming platforms
  const { data: streamingPlatforms } = useQuery({
    queryKey: ["streaming-platforms-all"],
    queryFn: () => streamingPlatformsApi.list({ page_size: 1000 }),
  });

  // Fetch directors with remote search
  const { data: directorsData, isLoading: directorsLoading } = useQuery({
    queryKey: ["directors-search", debouncedDirectorSearch],
    queryFn: () =>
      directorsApi.list({ search: debouncedDirectorSearch, page_size: 50 }),
  });

  // Merge search results with the currently selected director
  const directorOptions = useMemo(() => {
    const options = (directorsData?.items || []).map((d) => ({
      value: d.id,
      label: d.name,
    }));

    const currentDirector = work?.series_details?.director;
    if (
      currentDirector &&
      !options.find((o) => o.value === currentDirector.id)
    ) {
      options.push({
        value: currentDirector.id,
        label: currentDirector.name,
      });
    }

    return options;
  }, [directorsData, work]);

  // Fetch nationalities
  const { data: nationalities } = useQuery({
    queryKey: ["nationalities-all"],
    queryFn: () => nationalitiesApi.list({ page_size: 1000 }),
  });

  // Set form values when editing
  useEffect(() => {
    if (work) {
      form.setFieldsValue({
        title: work.title,
        original_title: work.original_title,
        original_language_id: work.original_language_id,
        release_year: work.release_year,
        description: work.description,
        external_url: work.external_url,
        // Series details
        // Extract IDs from streaming platform objects
        streaming_platform_id: work.series_details?.streaming_platform?.id,
        director_id: work.series_details?.director_id,
        nationality_id: work.series_details?.nationality_id,
        // Genre IDs
        genre_ids: work.genres?.map((g) => g.id) || [],
      });
      // Set cover image if exists
      if (work.cover_image_id && work.cover_image) {
        setCoverImageId(work.cover_image_id);
        setCoverImagePreview(imagesApi.getFileUrl(work.cover_image_id));
      }
    }
  }, [work, form]);

  const createMutation = useMutation({
    mutationFn: (data: WorkCreate) => worksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });
      message.success("Serial utworzony pomyślnie");
      navigate("/series");
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: (data: WorkUpdate) => worksApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["works"] });
      queryClient.invalidateQueries({ queryKey: ["work", id] });
      message.success("Serial zaktualizowany pomyślnie");
      navigate("/series");
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const createDirectorMutation = useMutation({
    mutationFn: (data: { name: string; nationality_id?: string }) =>
      directorsApi.create(data),
    onSuccess: (newDirector) => {
      queryClient.invalidateQueries({ queryKey: ["directors-all"] });
      form.setFieldValue("director_id", newDirector.id);
      message.success("Reżyser dodany pomyślnie");
      setIsDirectorModalOpen(false);
      directorForm.resetFields();
    },
    onError: () => message.error("Nie udało się dodać reżysera"),
  });

  const onFinish = (values: Record<string, unknown>) => {
    if (isReadOnly) return;

    const payload: WorkCreate | WorkUpdate = {
      type: "series",
      title: values.title as string,
      original_title: values.original_title as string | undefined,
      original_language_id: values.original_language_id as string | undefined,
      release_year: values.release_year as number | undefined,
      description: values.description as string | undefined,
      external_url: values.external_url as string | undefined,
      cover_image_id: coverImageId || undefined,
      genre_ids: values.genre_ids as string[] | undefined,
      series_details: {
        streaming_platform_id: values.streaming_platform_id as
          | string
          | undefined,
        director_id: values.director_id as string | undefined,
        nationality_id: values.nationality_id as string | undefined,
      },
    };

    if (isEditing) {
      updateMutation.mutate(payload as WorkUpdate);
    } else {
      createMutation.mutate(payload as WorkCreate);
    }
  };

  if (isEditing && workLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" tip="Ładowanie..." />
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSeriesDataAccept = async (seriesInfo: SeriesInfoResponse) => {
    const matchedGenreIds = seriesInfo.genre_ids || [];
    const matchedPlatformId = seriesInfo.streaming_platform_ids?.[0];

    // Upload cover image from URL if provided
    if (seriesInfo.cover_url) {
      try {
        setIsUploadingImage(true);
        const uploadedImage = await imagesApi.uploadFromUrl(
          seriesInfo.cover_url,
          seriesInfo.title || undefined,
        );
        setCoverImageId(uploadedImage.id);
        setCoverImagePreview(imagesApi.getFileUrl(uploadedImage.id));
        message.success("Okładka pobrana pomyślnie");
      } catch (error) {
        console.error("Failed to upload cover image:", error);
        message.warning("Nie udało się pobrać okładki");
      } finally {
        setIsUploadingImage(false);
      }
    }

    const formValues: Record<string, any> = {
      title: seriesInfo.title || undefined,
      original_title: seriesInfo.original_title || undefined,
      release_year: seriesInfo.start_year || undefined,
      description: seriesInfo.description || undefined,
      genre_ids: matchedGenreIds,
      streaming_platform_id: matchedPlatformId,
    };

    form.setFieldsValue(formValues);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Powrót
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          {isEditing
            ? isReadOnly
              ? "Podgląd Serialu"
              : "Edytuj Serial"
            : "Dodaj Serial"}
        </Title>
      </div>

      <Card>
        {/* AI Helper - only for new works and if not read only */}
        {!isEditing && <AISeriesHelper onAccept={handleSeriesDataAccept} />}

        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* Common Fields */}
          {isEditing && work?.slug && (
            <Form.Item label="Slug (URL)">
              <Typography.Text code copyable>
                {work.slug}
              </Typography.Text>
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="title"
                label="Tytuł"
                rules={[{ required: true, message: "Tytuł jest wymagany" }]}
              >
                <Input placeholder="Wprowadź tytuł" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="original_title" label="Tytuł oryginalny">
                <Input placeholder="Oryginalny tytuł (jeśli inny)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="release_year" label="Rok premiery">
                <InputNumber
                  min={1800}
                  max={2100}
                  placeholder="e.g. 2024"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="original_language_id" label="Język oryginalny">
                <Select placeholder="Wybierz język" allowClear>
                  {languages?.items.map((lang) => (
                    <Select.Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Okładka">
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {coverImagePreview && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <img
                        src={coverImagePreview}
                        alt="Okładka"
                        style={{
                          maxWidth: 150,
                          maxHeight: 200,
                          objectFit: "contain",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                        }}
                      />
                      {!isReadOnly && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          style={{ position: "absolute", top: 4, right: 4 }}
                          onClick={() => {
                            setCoverImageId(null);
                            setCoverImagePreview(null);
                          }}
                        />
                      )}
                    </div>
                  )}
                  {!isReadOnly && (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        try {
                          setIsUploadingImage(true);
                          const uploadedImage = await imagesApi.upload(file);
                          setCoverImageId(uploadedImage.id);
                          setCoverImagePreview(
                            imagesApi.getFileUrl(uploadedImage.id),
                          );
                          message.success("Okładka przesłana pomyślnie");
                        } catch (error) {
                          console.error("Failed to upload image:", error);
                          message.error("Nie udało się przesłać okładki");
                        } finally {
                          setIsUploadingImage(false);
                        }
                        return false;
                      }}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={isUploadingImage}
                      >
                        {coverImagePreview
                          ? "Zmień okładkę"
                          : "Wybierz okładkę"}
                      </Button>
                    </Upload>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="external_url" label="Link do źródła">
            <Input placeholder="np. https://filmweb.pl/serial/..." />
          </Form.Item>

          <Form.Item name="description" label="Opis">
            <TextArea rows={4} placeholder="Wprowadź opis..." />
          </Form.Item>

          {/* Series-specific fields */}
          <Divider>Szczegóły serialu</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="streaming_platform_id"
                label="Platforma streamingowa"
              >
                <Select
                  placeholder="Wybierz platformę"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {streamingPlatforms?.items.map((platform) => (
                    <Select.Option key={platform.id} value={platform.id}>
                      {platform.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="director_id" label="Reżyser">
                <Select
                  placeholder="Wybierz reżysera"
                  allowClear
                  showSearch
                  onSearch={setDirectorSearch}
                  filterOption={false}
                  loading={directorsLoading}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      {!isReadOnly && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => setIsDirectorModalOpen(true)}
                              block
                            >
                              Dodaj nowego reżysera
                            </Button>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                >
                  {directorOptions.map((director) => (
                    <Select.Option key={director.value} value={director.value}>
                      {director.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="genre_ids" label="Gatunki filmowe">
                <Select
                  mode="multiple"
                  placeholder="Wybierz gatunki filmowe"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {genres?.items.map((genre) => (
                    <Select.Option key={genre.id} value={genre.id}>
                      {genre.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="nationality_id" label="Kraj produkcji">
                <Select
                  placeholder="Wybierz kraj"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {nationalities?.items.map((nationality) => (
                    <Select.Option key={nationality.id} value={nationality.id}>
                      {nationality.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Actions */}
          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate(-1)}>
                {isReadOnly ? "Wróć" : "Anuluj"}
              </Button>
              {!isReadOnly && (
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSubmitting}
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
              {isReadOnly && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  disabled
                  title="Brak uprawnień do edycji"
                >
                  {isEditing ? "Zapisz zmiany" : "Dodaj"}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Seasons Manager - only in edit mode */}
      {isEditing && work?.series_details && (
        <SeasonsManager
          workId={id!}
          seasons={work.series_details.seasons || []}
          readOnly={isReadOnly}
        />
      )}

      {/* Additional Images - only in edit mode */}
      {isEditing && id && (
        <WorkImagesManager workId={id} readOnly={isReadOnly} />
      )}

      {/* Modal for adding new director */}
      <Modal
        title="Dodaj nowego reżysera"
        open={isDirectorModalOpen}
        onCancel={() => {
          setIsDirectorModalOpen(false);
          directorForm.resetFields();
        }}
        onOk={() => directorForm.submit()}
        confirmLoading={createDirectorMutation.isPending}
        okText="Dodaj"
        cancelText="Anuluj"
      >
        <Form
          form={directorForm}
          layout="vertical"
          onFinish={(values) => createDirectorMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="Imię i nazwisko"
            rules={[
              { required: true, message: "Imię i nazwisko jest wymagane" },
            ]}
          >
            <Input placeholder="np. Christopher Nolan" />
          </Form.Item>
          <Form.Item name="nationality_id" label="Narodowość">
            <Select
              placeholder="Wybierz narodowość"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {nationalities?.items.map((nat) => (
                <Select.Option key={nat.id} value={nat.id}>
                  {nat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SeriesFormPage;
